import React, { useState } from 'react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'

import { Button } from '@/components/buttons/Button'

import { ADDED, REMOVED, computeDiff, toUnifiedBlocks, toWords } from './diff.helpers'

import './UnifiedDiff.scss'

// Unchanged passages shorter than this are shown whole — collapsing two lines to save two lines is worse than not
// collapsing at all. Longer ones show a little context at each end so a change never appears without its setting.
const COLLAPSE_OVER_LINES = 6
const CONTEXT_LINES = 2

/** One changed passage, with only the words that actually moved marked. */
const ChangedBlock = ({ before, after }) => {
  const words = computeDiff(toWords(before), toWords(after))

  return (
    <p className="unified-diff__block">
      {words.map((word, index) => {
        const key = `${index}-${word.value}`

        if (word.type === REMOVED) {
          return (
            <del key={key} className="unified-diff__removed">
              {word.value}
            </del>
          )
        }

        if (word.type === ADDED) {
          return (
            <ins key={key} className="unified-diff__added">
              {word.value}
            </ins>
          )
        }

        return <span key={key}>{word.value}</span>
      })}
    </p>
  )
}

/** Untouched text: shown in full when short, otherwise trimmed to a little context with the rest on request. */
const UnchangedBlock = ({ lines, expanded, onExpand }) => {
  if (lines.length <= COLLAPSE_OVER_LINES || expanded) {
    return <p className="unified-diff__block unified-diff__context">{lines.join('\n')}</p>
  }

  const hidden = lines.length - CONTEXT_LINES * 2

  return (
    <>
      <p className="unified-diff__block unified-diff__context">
        {lines.slice(0, CONTEXT_LINES).join('\n')}
      </p>
      <Button text small onClick={onExpand} className="unified-diff__expand">
        Show {hidden} unchanged lines
      </Button>
      <p className="unified-diff__block unified-diff__context">
        {lines.slice(-CONTEXT_LINES).join('\n')}
      </p>
    </>
  )
}

/**
 * A prose diff: one column, changes marked word by word, untouched passages collapsed.
 *
 * The side-by-side line diff (`DiffModal`) is right for versioned settings and wrong for prose. A prompt is a few
 * very long lines, so rewording one sentence marks an entire paragraph removed and re-added — every changed
 * passage comes out fully red and fully green, which tells a reader nothing about what moved. Two narrow columns of
 * wrapped prose then make it harder still.
 *
 * So: unified rather than side by side, and word-level within each changed passage. The whole replaced passage is
 * compared at once rather than line against line, because rewriting one sentence as a four-item list is a normal
 * edit here and pairing it line-for-line would render it as an unrelated deletion beside an unrelated insertion.
 *
 * @param {string} props.oldText the text being replaced
 * @param {string} props.newText the proposed text
 * @param {string} props.emptyLabel what to say when the two are identical
 * @param {boolean} props.plain drop the panel framing and inherit the surrounding text, for a diff
 *   shown inline among other values rather than as a block of its own
 */
export const UnifiedDiff = ({
  oldText,
  newText,
  emptyLabel = 'No changes',
  missingLabel = 'The original is unavailable, so there is nothing to compare against.',
  plain = false,
}) => {
  const [expandedBlocks, setExpandedBlocks] = useState([])

  // With no original, every line is an addition and the whole thing renders solid green — which reads as "this was
  // rewritten from nothing" when the truth is that we lost the other half. Refuse rather than mislead.
  if (isEmpty(oldText) && !isEmpty(newText)) {
    return <p className="unified-diff__empty">{missingLabel}</p>
  }

  const blocks = toUnifiedBlocks(oldText, newText)
  const changed = blocks.filter((block) => block.type === 'changed')

  if (isEmpty(changed)) {
    return <p className="unified-diff__empty">{emptyLabel}</p>
  }

  return (
    <div className={classNames('unified-diff', { 'unified-diff--plain': plain })}>
      {blocks.map((block, index) => {
        const key = `${block.type}-${index}`

        if (block.type === 'changed') {
          return <ChangedBlock key={key} before={block.before} after={block.after} />
        }

        return (
          <UnchangedBlock
            key={key}
            lines={block.lines}
            expanded={expandedBlocks.includes(index)}
            onExpand={() => setExpandedBlocks([...expandedBlocks, index])}
          />
        )
      })}
    </div>
  )
}
